/**
 * OAuth onboarding for Google and Microsoft 365 — the Spark-style
 * "click once, never again" path, and the ONLY path for Microsoft.
 *
 * For Google this beats app passwords on every axis: no 2-Step Verification
 * prerequisite, nothing to copy and paste, and it works on Workspace accounts
 * whose admin has disabled app passwords (a sensible policy — app passwords are
 * static and bypass 2SV, OAuth grants are scoped and individually revocable).
 * For Microsoft there is no alternative at all: Exchange Online removed basic
 * authentication, so there is no app-password equivalent to fall back on.
 *
 * The consent flow opens in a popup rather than navigating away, so an open
 * draft survives the round trip to the provider.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type OAuthProviderId, type OAuthProviderInfo } from '../lib/api';
import styles from './ConnectAccount.module.css';

interface OAuthPopupMessage {
  source: 'kiln-oauth';
  ok: boolean;
}

function isOAuthMessage(data: unknown): data is OAuthPopupMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === 'kiln-oauth'
  );
}

/** Entra secrets lapse; start nagging while there is still time to act. */
const SECRET_WARN_DAYS = 30;

function daysUntil(epochSeconds: number): number {
  return Math.ceil((epochSeconds * 1000 - Date.now()) / 86_400_000);
}

function toEpochSeconds(dateInput: string): number | null {
  if (!dateInput) return null;
  const ms = Date.parse(`${dateInput}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

export default function ConnectAccount() {
  const queryClient = useQueryClient();
  const configQuery = useQuery({ queryKey: ['oauth-config'], queryFn: () => api.oauth.config() });

  const [provider, setProvider] = useState<OAuthProviderId>('google');
  const [showSetup, setShowSetup] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tenant, setTenant] = useState('');
  const [secretExpiry, setSecretExpiry] = useState('');
  const [hint, setHint] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);

  const providers: OAuthProviderInfo[] = useMemo(
    () => configQuery.data?.providers ?? [],
    [configQuery.data],
  );
  const current = providers.find((p) => p.id === provider) ?? null;
  const configured = current?.configured ?? false;
  const redirectUri = configQuery.data?.redirectUri ?? '';

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isOAuthMessage(event.data)) return;
      setConnecting(false);
      if (event.data.ok) {
        void queryClient.invalidateQueries({ queryKey: ['accounts'] });
        void queryClient.invalidateQueries({ queryKey: ['threads'] });
        setHint('');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [queryClient]);

  // A popup closed without posting back is a silent cancel; clear the spinner
  // rather than leaving the button stuck on "Waiting…" forever.
  useEffect(() => {
    if (!connecting) return;
    const timer = window.setInterval(() => {
      if (popupRef.current?.closed) {
        setConnecting(false);
        void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [connecting, queryClient]);

  const saveConfig = useMutation({
    mutationFn: () =>
      api.oauth.setConfig({
        provider,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        ...(provider === 'microsoft' && tenant.trim() ? { tenant: tenant.trim() } : {}),
        ...(provider === 'microsoft' ? { secretExpiresAt: toEpochSeconds(secretExpiry) } : {}),
      }),
    onSuccess: () => {
      setClientSecret('');
      setShowSetup(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['oauth-config'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.displayMessage : String(err)),
  });

  const connect = useCallback(async () => {
    setError(null);
    // Open the window synchronously, before any await. A popup opened from an
    // async continuation is no longer attributed to the click and gets blocked.
    const popup = window.open('about:blank', 'kiln-oauth', 'width=520,height=680');
    if (!popup) {
      setError('Your browser blocked the popup. Allow popups for this site and try again.');
      return;
    }
    popupRef.current = popup;
    setConnecting(true);
    try {
      const { url } = await api.oauth.start({
        provider,
        ...(hint.trim() ? { email: hint.trim() } : {}),
      });
      popup.location.href = url;
    } catch (err) {
      popup.close();
      setConnecting(false);
      setError(err instanceof ApiError ? err.displayMessage : String(err));
    }
  }, [provider, hint]);

  if (configQuery.isPending) return null;

  const expiryDays =
    current?.secretExpires && current.secretExpiresAt ? daysUntil(current.secretExpiresAt) : null;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h3 className={styles.title}>Add an account</h3>
        {configured && (
          <button type="button" className={styles.link} onClick={() => setShowSetup((v) => !v)}>
            {showSetup ? 'Hide setup' : 'Change client'}
          </button>
        )}
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Mail provider">
        {providers.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={p.id === provider}
            className={styles.tab}
            data-active={p.id === provider}
            onClick={() => {
              setProvider(p.id);
              setShowSetup(false);
              setError(null);
            }}
          >
            {p.label}
            {p.configured && <span className={styles.dot} aria-label="configured" />}
          </button>
        ))}
      </div>

      {/* Entra secrets lapse and take every Microsoft mailbox with them at once,
          so this warning earns its place well before the day itself. */}
      {expiryDays !== null && expiryDays <= SECRET_WARN_DAYS && (
        <p className={styles.warn}>
          {expiryDays > 0
            ? `This client secret expires in ${expiryDays} day${expiryDays === 1 ? '' : 's'}. `
            : 'This client secret has expired. '}
          Create a new one in the Entra portal and paste it below — every Microsoft mailbox stops
          refreshing when it lapses, and reconnecting individual accounts will not help.
        </p>
      )}

      {!configured && !showSetup && (
        <>
          <p className={styles.body}>
            {provider === 'microsoft'
              ? 'Microsoft 365 requires OAuth — Exchange Online removed basic authentication, so there is no app password to use instead.'
              : 'Approve the mailbox in Google, the way Spark does — no app password, no 2-Step Verification requirement.'}
          </p>
          <p className={styles.body}>
            Needs a one-time {provider === 'microsoft' ? 'Entra ID app registration' : 'Google Cloud OAuth client'}. See{' '}
            <code className={styles.code}>
              docs/{provider === 'microsoft' ? 'ENTRA-SETUP.md' : 'OAUTH-SETUP.md'}
            </code>
            .
          </p>
          <button type="button" className={styles.primary} onClick={() => setShowSetup(true)}>
            Set up {current?.label ?? provider}
          </button>
        </>
      )}

      {showSetup && (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            saveConfig.mutate();
          }}
        >
          {provider === 'google' ? (
            <p className={styles.warn}>
              In the Google Cloud console, publishing status must be <strong>In production</strong>,
              not Testing. Apps left in Testing get refresh tokens that expire after 7 days, which
              would mean reconnecting every mailbox every week.
            </p>
          ) : (
            <p className={styles.warn}>
              Entra client secrets <strong>expire</strong> — 24 months at most, 6 months if you take
              the portal default. Record the date below so Kiln can warn you before every Microsoft
              mailbox stops at once.
            </p>
          )}

          <label className={styles.label}>
            Redirect URI — paste this into the provider&rsquo;s console
            <input
              className={styles.input}
              readOnly
              value={redirectUri}
              onFocus={(e) => e.target.select()}
            />
          </label>

          <label className={styles.label}>
            {provider === 'microsoft' ? 'Application (client) ID' : 'Client ID'}
            <input
              className={styles.input}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={provider === 'microsoft' ? '00000000-0000-0000-0000-000000000000' : '…apps.googleusercontent.com'}
              autoComplete="off"
              spellCheck={false}
            />
          </label>

          <label className={styles.label}>
            Client secret {provider === 'microsoft' && '(the Value, not the Secret ID)'}
            <input
              className={styles.input}
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={provider === 'microsoft' ? '' : 'GOCSPX-…'}
              autoComplete="off"
              spellCheck={false}
            />
          </label>

          {provider === 'microsoft' && (
            <>
              <label className={styles.label}>
                Directory (tenant) ID — leave blank for multi-tenant
                <input
                  className={styles.input}
                  value={tenant}
                  onChange={(e) => setTenant(e.target.value)}
                  placeholder="common"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
              <label className={styles.label}>
                Secret expires on
                <input
                  className={styles.input}
                  type="date"
                  value={secretExpiry}
                  onChange={(e) => setSecretExpiry(e.target.value)}
                />
              </label>
            </>
          )}

          <div className={styles.row}>
            <button
              type="submit"
              className={styles.primary}
              disabled={saveConfig.isPending || !clientId.trim() || !clientSecret.trim()}
            >
              {saveConfig.isPending ? 'Saving…' : 'Save'}
            </button>
            {configured && (
              <button type="button" className={styles.ghost} onClick={() => setShowSetup(false)}>
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {configured && !showSetup && (
        <>
          <p className={styles.body}>
            {provider === 'google' ? (
              <>
                Approve the mailbox in Google. You will see a &ldquo;Google hasn&rsquo;t verified
                this app&rdquo; screen once per account — choose{' '}
                <strong>Advanced → Go to Kiln</strong>. That is expected for a personal, unverified
                app and does not affect how long the connection lasts.
              </>
            ) : (
              <>
                Approve the mailbox in Microsoft. If your tenant requires admin consent for mail
                scopes, an administrator has to approve the app once — after that every mailbox in
                the tenant connects without further prompts.
              </>
            )}
          </p>
          <div className={styles.row}>
            <input
              className={styles.input}
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="which address? (optional)"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              className={styles.primary}
              onClick={() => void connect()}
              disabled={connecting}
            >
              {connecting ? 'Waiting…' : `Continue with ${current?.label ?? provider}`}
            </button>
          </div>
        </>
      )}

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
